// Models the company lets people call through the Hub gateway (앱 연결 → 쓸 수 있는 AI 모델). People never type model
// names: the built-in list below is offered by default, administrators switch models on and off in bulk, add models a
// release does not know yet (by ID or fetched from the provider with the company key) and set per-person limits.
import { z } from 'zod';
import { EpochMs } from '../common.js';
import { PROVIDERS, type Provider } from '../enums.js';

/** Provider model IDs (stored in requests.model, varchar 120). */
export const GatewayModelId = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:/-]{0,79}$/);
export interface CatalogModel { id: string; label: string }

/**
 * Current chat models, newest first, shipped with each Hub release. With "최신 모델 자동 추가" on (the default) every
 * model listed here can be called without an administrator listing it, so a release that adds a model offers it at
 * once. Confirmed in each provider's GET /v1/models through the Hub gateway on 2026-10-27. The client picker
 * (packages/client-core/src/models.ts RECENT_MODELS) shows the same list: update both together.
 */
export const GATEWAY_MODEL_CATALOG: Readonly<Record<Provider, readonly CatalogModel[]>> = {
  anthropic: [
    { id: 'claude-fable-5-1', label: 'Claude Fable 5.1' },
    { id: 'claude-opus-5-5', label: 'Claude Opus 5.5' },
    { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
    { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5' },
  ],
  openai: [
    { id: 'gpt-6.1-terra', label: 'GPT-6.1 Terra' },
    { id: 'gpt-6.1-sol', label: 'GPT-6.1 Sol' },
    { id: 'gpt-6-astra', label: 'GPT-6 Astra' },
    { id: 'gpt-6-sol', label: 'GPT-6 Sol' },
    { id: 'gpt-6-luna', label: 'GPT-6 Luna' },
  ],
};

/** Per person, per model. Days and months follow the organisation time zone. null = no limit. */
export const GatewayModelLimits = z.object({
  dailyRequestsPerUser: z.number().int().min(1).max(1_000_000).nullable(),
  monthlyTokensPerUser: z.number().int().min(1_000).max(100_000_000_000).nullable(),
}).strict();
export type GatewayModelLimits = z.infer<typeof GatewayModelLimits>;
export const NO_MODEL_LIMITS: GatewayModelLimits = { dailyRequestsPerUser: null, monthlyTokensPerUser: null };

/** catalog = built-in list · provider = fetched from the provider with the company key · manual = added by ID. */
export const GatewayModelSource = z.enum(['catalog', 'provider', 'manual']);
export const GatewayModelSetting = z.object({ id: GatewayModelId, enabled: z.boolean(), source: GatewayModelSource, limits: GatewayModelLimits }).strict();
export type GatewayModelSetting = z.infer<typeof GatewayModelSetting>;
export const GatewayProviderPolicy = z.object({
  /** Built-in models a release adds are switched on automatically (models switched off stay off). */
  followLatest: z.boolean(),
  /** Any well-formed model name may be called (the gateway's `*`); limits still apply to listed models. */
  allowAny: z.boolean(),
  models: z.array(GatewayModelSetting).max(200),
}).strict().superRefine((p, ctx) => {
  if (new Set(p.models.map((m) => m.id)).size !== p.models.length) ctx.addIssue({ code: 'custom', message: 'duplicate model', path: ['models'] });
});
export type GatewayProviderPolicy = z.infer<typeof GatewayProviderPolicy>;
/** org_settings `gateway.modelPolicy` and the body of PUT /api/v1/admin/gateway-models. */
export const GatewayModelPolicy = z.object({ anthropic: GatewayProviderPolicy, openai: GatewayProviderPolicy }).strict();
export type GatewayModelPolicy = z.infer<typeof GatewayModelPolicy>;

export const GatewayModelRow = GatewayModelSetting.extend({
  label: z.string(),
  inCatalog: z.boolean(),
  /** A built-in model the saved settings do not mention yet while automatic addition is off (shown switched off). */
  isNew: z.boolean(),
});
export type GatewayModelRow = z.infer<typeof GatewayModelRow>;
export const GatewayProviderView = z.object({
  followLatest: z.boolean(),
  allowAny: z.boolean(),
  /** false = nothing saved yet: the built-in list applies as it is. */
  customized: z.boolean(),
  models: z.array(GatewayModelRow),
  /** What the gateway accepts now (`*` when any model is allowed). */
  allowed: z.array(z.string()),
});
export type GatewayProviderView = z.infer<typeof GatewayProviderView>;
/** GET /api/v1/connect/gateway-models — everyone sees what may be called and the limits; administrators edit it. */
export const GatewayModelPolicyView = z.object({ anthropic: GatewayProviderView, openai: GatewayProviderView, canManage: z.boolean(), updatedAt: EpochMs.nullable() });
export type GatewayModelPolicyView = z.infer<typeof GatewayModelPolicyView>;

/** POST /api/v1/admin/gateway-models/discover — the provider's current model list, read with the company key (not saved). */
export const GatewayModelDiscoverRequest = z.object({ provider: z.enum(PROVIDERS) }).strict();
export const GatewayModelDiscoverResponse = z.object({ provider: z.enum(PROVIDERS), models: z.array(z.object({ id: GatewayModelId, label: z.string() })).max(200) });

/** The gateway's "any model" marker in an allow-list. */
export const ANY_GATEWAY_MODEL = '*';

/**
 * Saved settings merged with the built-in list. `saved` is `gateway.modelPolicy` for the provider (null = never saved);
 * `legacy` is the allow-list stored before (org_settings `gateway.models.<provider>`, undefined = no row). A legacy
 * list keeps meaning exactly what it meant (only those models; `*` = any), and an explicitly empty one still denies.
 */
export function resolveGatewayModels(provider: Provider, saved: GatewayProviderPolicy | null, legacy: unknown): GatewayProviderView {
  const catalog = GATEWAY_MODEL_CATALOG[provider];
  let base: GatewayProviderPolicy; let customized = true;
  if (saved) base = saved;
  else if (Array.isArray(legacy)) {
    const ids = [...new Set(legacy.filter((x): x is string => typeof x === 'string' && GatewayModelId.safeParse(x).success))];
    base = { followLatest: false, allowAny: legacy.includes(ANY_GATEWAY_MODEL), models: ids.map((id) => ({ id, enabled: true, source: catalog.some((c) => c.id === id) ? 'catalog' : 'manual', limits: NO_MODEL_LIMITS })) };
  } else { base = { followLatest: true, allowAny: false, models: [] }; customized = false; }
  const label = (id: string) => catalog.find((c) => c.id === id)?.label ?? id;
  const listed = new Map(base.models.map((m) => [m.id, m]));
  const rows: GatewayModelRow[] = catalog.map((c) => {
    const s = listed.get(c.id);
    return s ? { ...s, label: c.label, inCatalog: true, isNew: false }
      : { id: c.id, enabled: base.followLatest, source: 'catalog', limits: NO_MODEL_LIMITS, label: c.label, inCatalog: true, isNew: customized && !base.followLatest };
  });
  for (const s of base.models) if (!catalog.some((c) => c.id === s.id)) rows.push({ ...s, label: label(s.id), inCatalog: false, isNew: false });
  return { followLatest: base.followLatest, allowAny: base.allowAny, customized, models: rows,
    allowed: [...rows.filter((r) => r.enabled).map((r) => r.id), ...(base.allowAny ? [ANY_GATEWAY_MODEL] : [])] };
}

export const gatewayModelSchemas = { GatewayModelPolicy, GatewayModelPolicyView, GatewayModelDiscoverRequest, GatewayModelDiscoverResponse } as const;
